import { Button, Card, Divider, Icons, Link, Stack, Table, Typography } from '@healthinal/ui';
import { useEffect, useState } from 'react';
import { useGetLabOrdersByPatientSuspense } from '../../../../../api/generated/lab-order-controller/lab-order-controller.ts';
import {
  LabOrderDto,
  LabOrderDtoInterpretation,
  LabOrderDtoType,
  LabOrderResultDtoType,
} from '../../../../../api/generated/model';
import { formatDateWithOptionalTime } from '../../../../../utils/date.ts';
import {
  compareLabOrdersByLatest,
  getLabOrderResultInterpretationName,
  getLabOrderResultName,
  getLabOrderTypeName,
} from '../../../../../utils/labOrder.ts';
import { LabOrderDetailsDialog } from '../../../../components/lab-order-results/LabOrderDetailsDialog.tsx';
import { SelectAppointmentForLabOrderResultDialog } from '../../../../components/lab-order-results/SelectAppointmentForLabOrderResultDialog.tsx';

export function LabOrderResultList({ patientId }: { patientId: string }) {
  const { data: labOrders } = useGetLabOrdersByPatientSuspense(patientId);
  const [isCreateLabOrderResultDialogOpen, setIsCreateLabOrderResultDialogOpen] = useState(false);
  const [labOrderForDetailsDialog, setLabOrderForDetailsDialog] = useState<LabOrderDto | null>(null);

  useEffect(() => {
    if (labOrderForDetailsDialog) {
      setLabOrderForDetailsDialog(labOrders.find((order) => order.id === labOrderForDetailsDialog.id) ?? null);
    }
  }, [labOrders]);
  return (
    <>
      <Card>
        <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
          <Typography level={'title-lg'}>Durchgeführte Test</Typography>
          <Button onClick={() => setIsCreateLabOrderResultDialogOpen(true)} endDecorator={<Icons.Add />}>
            Test hinzufügen
          </Button>
        </Stack>
        <Divider />
        {labOrders.length ? (
          <Table>
            <thead>
              <tr>
                <th>Datum</th>
                <th>Test-Typ</th>
                <th>Resultat</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {[...labOrders].sort(compareLabOrdersByLatest).map((labOrder) => (
                <tr key={labOrder.id}>
                  <td>{formatDateWithOptionalTime(labOrder.samplingDate, labOrder.samplingTime)}</td>
                  <td>{getLabOrderTypeName(labOrder.type)}</td>
                  <td>{getResultInterpretationName(labOrder)}</td>
                  <td style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Link onClick={() => setLabOrderForDetailsDialog(labOrder)} endDecorator={<Icons.ArrowForward />}>
                      Anzeigen
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          <Stack alignItems={'center'} gap={1} p={3}>
            <Icons.Description />
            <Typography level={'body-sm'}>Noch keine Tests vorhanden</Typography>
          </Stack>
        )}
      </Card>
      <SelectAppointmentForLabOrderResultDialog
        open={isCreateLabOrderResultDialogOpen}
        onClose={() => setIsCreateLabOrderResultDialogOpen(false)}
        patientId={patientId}
      />
      {labOrderForDetailsDialog && (
        <LabOrderDetailsDialog
          open={!!labOrderForDetailsDialog}
          onClose={() => setLabOrderForDetailsDialog(null)}
          labOrder={labOrderForDetailsDialog}
        />
      )}
    </>
  );
}

function getResultInterpretationName(labOrder: LabOrderDto) {
  if (labOrder.type !== LabOrderDtoType.XRAY) {
    return (
      <Typography color={labOrder.interpretation === LabOrderDtoInterpretation.PENDING ? 'warning' : undefined}>
        {getLabOrderResultInterpretationName(labOrder.interpretation)}
      </Typography>
    );
  }

  const xrayFindingResult = labOrder.results.find((result) => result.type === LabOrderResultDtoType.XRAY_FINDING);
  if (xrayFindingResult) {
    return getLabOrderResultName(xrayFindingResult.type, xrayFindingResult.result);
  }

  return (
    <Typography color={'warning'}>{getLabOrderResultInterpretationName(LabOrderDtoInterpretation.PENDING)}</Typography>
  );
}
